import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import Swal from 'sweetalert2';
import Avatar from '@mui/material/Avatar';
import isEmpty from 'lodash/isEmpty';
import { loadAgentPhoto, setCurrentAgent } from '@redux/modules/agent';
import { loadFormDefinition } from '@redux/modules/formGeneration';
import { getAgentSelected, selectAgentPreferredPhoto, selectAgentPreferredPhotoDetails } from '@redux/selectors/AgentSelector';
import { getUserName } from '@redux/selectors/UserSelector';
import { addTeamMember, removeTeamMember, searchTeamMember } from '@redux/modules/teamsManagement';
import { selectCanBeImpersonatedBy, selectCanImpersonate } from '@redux/selectors/TeamsManagementSelector';
import Moment from 'react-moment';

import useRowLock from '@hooks/useRowLock';
import useAcl from '@hooks/useAcl';

import TeamMembersTable from '@components/table/TeamMembersTable';
import TeamLeadersTable from '@components/table/TeamLeadersTable';
import AgentLookupModal from '@components/form/formComponents/inputGroups/agentLookup/AgentLookupModal';
import Button from '@components/smartAdminUI/Button';
import Panel from '@components/smartAdminUI/Panel';
import ViewLoader from '@components/loaders/ViewLoader';
// import TierModal from '@components/modal/TierModal';

// import Alert from 'react-bootstrap/Alert';
import StatusBadges from '@components/badges/StatusBadges';
// import Collapse from 'react-bootstrap/Collapse';

const operationType = 'edit';
const definitionType = 'agent';

const ViewAgents = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const currAgent = useSelector(getAgentSelected);
  const username = useSelector(getUserName);
  const currentAgentPhotoDetails = useSelector(selectAgentPreferredPhotoDetails);
  const currentAgentPhoto = useSelector(selectAgentPreferredPhoto);
  const canBeImpersonatedByData = useSelector(selectCanBeImpersonatedBy);
  const canImpersonateData = useSelector(selectCanImpersonate);
  const memberName = useMemo(() => currAgent?.MemberFullName ?? currAgent?.MemberFirstName.concat(' ', currAgent?.MemberLastName), [currAgent]);

  const [currentAgentPhotoURL, setCurrentAgentPhotoURL] = useState(null);
  const [editButtonClicked, setEditButtonClicked] = useState(false);
  // const [open, setOpen] = useState(false);

  const [urlParam, setUrlParam] = useQueryParam('UniqueID', StringParam);

  // const [tierModalOpen, setTierModalOpen] = useState(false);

  const jsonBody = {
    action: 'select',
    where: {
      clause: [],
    },
  };

  const rowLockEntity = useMemo(() => ({ type: 'agent', id: currAgent?.UniqueID }), [currAgent]);

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    lockedByMeWithinSession,
    impersonatedBy,
    isAdmin,
    lastUpdatedOn,
    lastCheckedOn,
    checkStatus,
    createLock,
    discardChanges,
    LockMessage,
  } = useRowLock({ rowLockEntity });

  const getCurrentAgentPhoto = photoURL => {
    dispatch(loadAgentPhoto(photoURL));
  };

  const onEditAgent = () => {
    if (currAgent) {
      dispatch(loadFormDefinition(operationType, definitionType, null, currAgent.UniqueID, 'agentACL'));
    }
  };

  const onClickEdit = () => {
    setEditButtonClicked(true);
    createLock(onEditAgent);
  };

  const {
    getAgentAcl,
    canEditForm,
    canAddTeamMember, canAddToTeam, canViewTeam, canEditTeam,
    isAllComplete,
  } = useAcl();
  const isAllAclComplete = isAllComplete();

  useEffect(() => {
    if (urlParam) {
      jsonBody.where.clause.push({ name: 'UniqueID', value: urlParam });
      dispatch(setCurrentAgent(jsonBody));
    }
  }, []);

  /*
    This has to be done using location. Currently, when the user loads a form, it redirects the user to the
      create/edit page. When this happens, it clears out the path and search values in the location object.
      We can't memo the location and use that as a comparison, because technically the locations will be
      the same on the reload, so we can't reset the search value in the location object.
  */
  useEffect(() => {
    if (location?.search === '') {
      if (currAgent) {
        setUrlParam(currAgent?.UniqueID, 'replaceIn');
      }
    }
  }, [location]);

  useEffect(() => {
    if (currAgent?.UniqueID) {
      // Retrieve the teams details for current agent.
      dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));

      // retrieves EDIT and COPY permissions for agent
      getAgentAcl({
        action: 'view',
        formname: 'Agent',
        uniqueId: currAgent.UniqueID,
      });

      // retrieves form permissions for agent
      getAgentAcl({
        action: 'forms',
        formname: 'Agent',
        uniqueId: currAgent.UniqueID,
      });

      if (location?.search === '' || location?.search !== currAgent?.UniqueID) {
        setUrlParam(currAgent?.UniqueID, 'replaceIn');
      }
    }
  }, [currAgent]);

  useEffect(() => {
    if (currentAgentPhotoDetails?.URL) {
      getCurrentAgentPhoto(currentAgentPhotoDetails.URL);
    }
  }, [currentAgentPhotoDetails]);

  useEffect(() => {
    if (currentAgentPhotoURL) URL.revokeObjectURL(currentAgentPhotoURL);
    if (currentAgentPhoto) setCurrentAgentPhotoURL(URL.createObjectURL(currentAgentPhoto));
    return () => currentAgentPhotoURL && URL.revokeObjectURL(currentAgentPhotoURL);
  }, [currentAgentPhoto]);

  const onAddMemberToCurrentAgentTeam = async member => {
    if (!(currAgent?.UniqueID && member?.UniqueID)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Adding Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(addTeamMember({ targetMemberkey: member.UniqueID, targetTeamKey: currAgent.UniqueID }));

      if (success) {
        Swal.fire({
          position: 'center',
          title: 'Member Added',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Added',
          html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onAddMemberToCurrentAgentTeam', `${member.MemberFullName} Could not be added. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Added',
        html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onAddMemberToCurrentAgentTeam', err);
    }
  };

  const onAddCurrentAgentToTeam = async member => {
    if (!(currAgent?.UniqueID && member?.UniqueID)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Adding Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(addTeamMember({ targetMemberkey: currAgent.UniqueID, targetTeamKey: member.UniqueID }));

      if (success) {
        Swal.fire({
          position: 'center',
          title: 'Member Added',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Added',
          html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onAddCurrentAgentToTeam', `${currAgent.MemberFullName} Could not be added. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Added',
        html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onAddCurrentAgentToTeam', err);
    }
  };

  const onRemoveMemberFromCurrentAgentTeam = async member => {
    if (!(currAgent?.UniqueID && member?.TeamMemberKeyNumeric)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Removing Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(removeTeamMember({ targetMemberkey: member.TeamMemberKeyNumeric, targetTeamKey: currAgent.UniqueID }));

      Swal.fire({
        position: 'center',
        title: 'Member Removed',
        icon: 'success',
        iconColor: 'var(--theme-success)',
        showConfirmButton: false,
        timer: 3000,
      });

      if (success) {
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Removed',
          html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onRemoveMemberFromCurrentAgentTeam', `${member.MemberFullName} Could not be removed. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Removed',
        html: `${member.MemberFullName} could not be removd at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onRemoveMemberFromCurrentAgentTeam', err);
    }
  };

  const onRemoveCurrentAgentFromTeam = async member => {
    if (!(currAgent?.UniqueID && member?.TeamLeadKeyNumeric)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Removing Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(removeTeamMember({ targetMemberkey: currAgent.UniqueID, targetTeamKey: member.TeamLeadKeyNumeric }));

      if (success) {
        // Refresh the teams details for current agent.
        Swal.fire({
          position: 'center',
          title: 'Member Removed',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Removed',
          html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onRemoveCurrentAgentFromTeam', `${currAgent.MemberFullName} Could not be removed. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Removed',
        html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onRemoveCurrentAgentFromTeam', err);
    }
  };

  // const TieredBillingAlertMessage = () => (
  //   <>
  //     <p>More details will be posted here and through your local board in the coming weeks, however, in the meantime, below are a few key questions that we&apos;ve already answered for you:</p>
  //     <ol>
  //       <li className="mb-2">
  //         <strong>What service tier will I be defaulted into in 2024?</strong>
  //         <p>
  //           All members will be put into the MLS Elite (full access) tier so that no work
  //           done to date within Prospects CRM Web/Mobile or Cloud CMA will be lost during the
  //           transition.
  //         </p>
  //       </li>
  //       <li className="mb-2">
  //         <strong className="color-danger-400">What are the risks in moving from the MLS Elite (full access) tier down to MLS Basic tier?</strong>
  //         <p>
  //           <strong className="color-danger-400">
  //             If you choose to move from the MLS Elite tier down to MLS Basic tier, it should be noted that&nbsp;
  //             all of your previous work within Prospects Web/Mobile and/or Cloud CMA will be lost and will not be
  //             recoverable once your user ID is removed for these MLS Elite tools.
  //           </strong>
  //         </p>
  //       </li>
  //       <li className="mb-2">
  //         <strong>What do I gain by staying with the MLS Elite (full access) tier?</strong>
  //         <p>
  //           By choosing the MLS Elite tier you will have access to the full suite of sales management tools
  //           including Prospects CRM Web, Prospects CRM Mobile and Cloud CMA at the lowest cost, along with any
  //           new tools and innovations that the Pillar 9&trade; development team launches in the future.
  //         </p>
  //       </li>
  //       <li className="mb-2">
  //         <strong>What are the fees that my board will charge me based on the choice I make for the tools I want?</strong>
  //         <p>
  //           Please see your local board communications for 2024 fee details.
  //         </p>
  //       </li>
  //     </ol>
  //     <p>See what&apos;s included in each tier of Pillar 9&trade; technology & business management tools by clicking the button below:</p>
  //     <Button variant="info" onClick={() => setTierModalOpen(true)}>
  //       Tiers of Services
  //     </Button>
  //   </>
  // );

  if (currAgent) {
    return (
      <>
        <div className="subheader">
          <h1 className="subheader-title">
            <i className="subheader-icon fal fa-user mr-2" />
            View
            <span className="fw-300 ml-2 mr-2">Agent</span>
          </h1>
        </div>
        {!isAllAclComplete && !canEditForm ? (
          <ViewLoader type={definitionType} />
        ) : (
          <>
            {((lockedByMeWithinSession && !editButtonClicked) || (locked && !lockingAuthorized && !editButtonClicked)) && (
              <LockMessage
                rowLockEntity={rowLockEntity}
                lockedBy={lockedBy}
                lockedByMeWithinSession={lockedByMeWithinSession}
                impersonatedBy={impersonatedBy}
                userName={username}
                isAdmin={isAdmin}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onDiscard={discardChanges}
                displayActions={canEditForm}
              />
            )}
            {/* <TierModal show={tierModalOpen} handleClose={() => setTierModalOpen(false)} /> */}
            {/* <Alert variant="info"> */}
            {/*  <div className="d-flex justify-content-between align-items-center"> */}
            {/*    <Alert.Heading>Pillar 9&trade; Technology & Business Management Tools – Choose your tier of services for 2024</Alert.Heading> */}
            {/*    <Button */}
            {/*      name="btnReadMoreNotifications" */}
            {/*      key="tieredBilling" */}
            {/*      variant="warning" */}
            {/*      onClick={() => setOpen(!open)} */}
            {/*      aria-controls="tieredBilling" */}
            {/*      aria-expanded={open} */}
            {/*      className="mt-1 mb-2" */}
            {/*      size="sm" */}
            {/*    > */}
            {/*      {open ? ( */}
            {/*        <> */}
            {/*          Read Less */}
            {/*          <i className="far fa-chevron-up ml-2" /> */}
            {/*        </> */}
            {/*      ) : ( */}
            {/*        <> */}
            {/*          Read More */}
            {/*          <i className="far fa-chevron-down ml-2" /> */}
            {/*        </> */}
            {/*      )} */}
            {/*    </Button> */}
            {/*  </div> */}
            {/*  <> */}
            {/*    <p> */}
            {/*      Starting in 2024, Pillar 9&trade; is offering members more choices in how you access the */}
            {/*      tools and technology best suited for your business. Soon, you will be */}
            {/*      able to come back here and choose the tier of business tools that best suits your needs. */}
            {/*    </p> */}
            {/*    <Collapse in={open}> */}
            {/*      <div id="tieredBilling"> */}
            {/*        <TieredBillingAlertMessage /> */}
            {/*      </div> */}
            {/*    </Collapse> */}
            {/*  </> */}
            {/* </Alert> */}
            <div className={locked ? 'row disabled' : 'row'}>
              <div className="col-lg-4 d-flex">
                <Panel customClass="w-100">
                  <div className="d-flex d-column align-items-center justify-content-center p-4">
                    <Avatar
                      sx={{
                        maxWidth: '250px',
                        height: '250px',
                        width: '100%',
                        bgcolor: 'var(--theme-secondary)',
                      }}
                      alt={memberName}
                      src={currentAgentPhotoURL}
                    />
                  </div>
                  <h3 className="mb-0 text-center">{memberName}</h3>
                  <h4 className="mb-0 text-center">{currAgent.OfficeName}</h4>
                  <p className="mt-3 mb-3 text-center">
                    <StatusBadges status={currAgent.MemberStatus} />
                    <span className="ml-2">{currAgent.MemberMlsId}</span>
                  </p>
                  { !isEmpty(currAgent.MemberDirectPhone) && (
                    <p className="mb-0 text-center mt-2">
                      <a href={`phone: ${currAgent.MemberDirectdPhone} `}>
                        <i className="far fa-phone-rotary mr-2" />
                        {currAgent.MemberDirectPhone}
                      </a>
                    </p>
                  )}
                  { !isEmpty(currAgent.MemberEmail) && (
                  <p className="mb-0 text-center mt-0">
                    <a href={`mailto: ${currAgent.MemberEmail} `}>
                      <i className="far fa-envelope mr-2" />
                      {currAgent.MemberEmail}
                    </a>
                  </p>
                  )}
                </Panel>
              </div>
              <div className="col-lg-8 d-flex">
                <Panel
                  header="Agent Details"
                  customClass="w-100"
                  panelToolbar={(
                    <>
                      {canEditForm && (
                        <Button
                          name="btnEditAgent"
                          variant="primary"
                          onClick={onClickEdit}
                          disabled={locked && !lockingAuthorized}
                        >
                          <i className="far fa-edit mr-2" />
                          Edit
                        </Button>
                      )}
                    </>
                  )}
                >
                  <table className="table borderless">
                    <tbody>
                      { currAgent.LanguageSpoken && (
                        <tr>
                          <th>Languages</th>
                          <td>{currAgent.LanguageSpoken}</td>
                        </tr>
                      )}
                      { currAgent.MemberAddress1 && (
                        <tr>
                          <th>Address</th>
                          <td>{currAgent.MemberAddress1}</td>
                        </tr>
                      )}
                      { currAgent.MemberCity && (
                        <tr>
                          <th>City</th>
                          <td>{currAgent.MemberCity}</td>
                        </tr>
                      )}
                      { currAgent.MemberPostalCode && (
                        <tr>
                          <th>Postal Code</th>
                          <td>{currAgent.MemberPostalCode}</td>
                        </tr>
                      )}
                      { currAgent.CellPhone && (
                        <tr>
                          <th>Mobile</th>
                          <td>
                            <a href={`tel: ${currAgent.CellPhone} `}>
                              <i className="far fa-mobile-alt mr-2" />
                              {currAgent.CellPhone}
                            </a>
                          </td>
                        </tr>
                      )}
                      { currAgent.MemberFax && (
                        <tr>
                          <th>Fax</th>
                          <td>
                            <a href={`tel: ${currAgent.MemberFax} `}>
                              <i className="far fa-fax mr-2" />
                              {currAgent.MemberFax}
                            </a>
                          </td>
                        </tr>
                      )}
                      { currAgent.PrimaryWebsite && (
                        <tr>
                          <th>Website</th>
                          <td>
                            <a href={currAgent.PrimaryWebsite} alt="Primary Website" target="_blank" rel="noreferrer">
                              <i className="far fa-globe mr-2" />
                              {currAgent.PrimaryWebsite}
                            </a>
                          </td>
                        </tr>
                      )}
                      { currAgent.OfficePhone && (
                        <tr>
                          <th>Office Phone</th>
                          <td>
                            <a href={`tel: ${currAgent.OfficePhone} `}>
                              <i className="far fa-phone-office mr-2" />
                              {currAgent.OfficePhone}
                            </a>
                          </td>
                        </tr>
                      )}
                      { currAgent.MembershipDate && (
                        <tr>
                          <th>Membership Date</th>
                          <td><Moment format="LL">{currAgent.MembershipDate}</Moment></td>
                        </tr>
                      )}
                      { currAgent.TerminationDate && (
                        <tr>
                          <th>Termination Date</th>
                          <td><Moment format="LL">{currAgent.TerminationDate}</Moment></td>
                        </tr>
                      )}
                      { (currAgent.WebLinkedIn || currAgent.WebFaceook || currAgent.WebTwitter || currAgent.MemberInstagram) && (
                      <tr>
                        <th>Social</th>
                        <td>
                          { currAgent.WebLinkedIn && <a href={currAgent.WebLinkedIn} className="mr-3" alt="LinkedIn" target="_blank" rel="noreferrer"><i className="fab fa-linkedin" /></a> }
                          { currAgent.WebFacebook && <a href={currAgent.WebFacebook} className="mr-3" alt="Facebook" target="_blank" rel="noreferrer"><i className="fab fa-facebook" /></a> }
                          { currAgent.WebTwitter && <a href={currAgent.WebTwitter} className="mr-3" alt="Twitter" target="_blank" rel="noreferrer"><i className="fab fa-x-twitter" /></a> }
                          { currAgent.MemberInstagram && <a href={currAgent.MemberInstagram} className="mr-3" alt="Instagram" target="_blank" rel="noreferrer"><i className="fab fa-instagram" /></a> }
                        </td>
                      </tr>
                      )}
                    </tbody>
                  </table>
                </Panel>
              </div>
            </div>
            <div className={locked && !lockingAuthorized ? 'row disabled' : 'row'}>
              {canViewTeam && (
                <>
                  {canEditTeam && (
                    <>
                      <AgentLookupModal
                        id="canBeImpersonatedByLookupModal"
                        onChange={onAddMemberToCurrentAgentTeam}
                        getCurrentImpersonationData
                      />
                      <AgentLookupModal
                        id="canImpersonateLookupModal"
                        onChange={onAddCurrentAgentToTeam}
                        getCurrentImpersonationData
                      />
                    </>
                  )}
                  <div className="col-lg-6 d-flex">
                    <Panel
                      customClass="w-100"
                      header="My Impersonations"
                      panelToolbar={(
                        canAddToTeam && (
                          <Button
                            name="btnAddImpersonationsAgent"
                            variant="primary"
                            data-toggle="modal"
                            data-target="#canImpersonateLookupModal"
                            disabled={locked && !lockingAuthorized}
                          >
                            <i className="far fa-user-plus mr-2" aria-hidden="true" />
                            Add
                          </Button>
                        )
                      )}
                    >
                      <p>Who this agent can impersonate:</p>
                      <TeamLeadersTable
                        displayData={canImpersonateData}
                        onMemberDelete={onRemoveCurrentAgentFromTeam}
                      />
                    </Panel>
                  </div>
                  <div className="col-lg-6 d-flex">
                    <Panel
                      customClass="w-100"
                      header="My Impersonators"
                      panelToolbar={(
                        canAddTeamMember && (
                          <Button
                            name="btnAddImpersonatorsAgent"
                            variant="primary"
                            data-toggle="modal"
                            data-target="#canBeImpersonatedByLookupModal"
                            disabled={locked && !lockingAuthorized}
                          >
                            <i className="far fa-user-plus mr-2" aria-hidden="true" />
                            Add
                          </Button>
                        )
                      )}
                    >
                      <p>Who can impersonate this agent:</p>
                      <TeamMembersTable
                        displayData={canBeImpersonatedByData}
                        onMemberDelete={onRemoveMemberFromCurrentAgentTeam}
                      />
                    </Panel>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </>
    );
  }
  return null;
};

export default ViewAgents;
